import { useEffect } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import mrDummyCharacterUrl from '../../assets/mesh/dummycharater1.glb';
import footReferenceAssetUrl from '../../assets/mesh/dummyfoot.glb';

/**
 * Editor-only reference (dummy) character shown at the grid center for VR and MR
 * projects. It gives authors a human-scale anchor while placing assets.
 *
 * It is intentionally NOT registered in Redux, the asset list, selection,
 * history, poly count, or the publish payload — it exists only in the live scene.
 * The name/userData flags below (`mrCenterCube` / `isMrCenterCube`) are reused so
 * all existing skip conditions across the app keep excluding it.
 */
const REFERENCE_OBJECT_NAME = 'mrCenterCube';

/** Label shown for the reference object in the left panel. */
export const REFERENCE_OBJECT_LABEL = 'Reference';

/**
 * Fired on `window` whenever the reference object is added, removed, or has its
 * visibility toggled. The left panel listens for this to sync its Reference row.
 * detail: { present: boolean, visible: boolean }
 */
export const REFERENCE_OBJECT_EVENT = 'referenceObjectChanged';

/** Project categories that should display the reference character. */
const REFERENCE_OBJECT_CATEGORIES = new Set(['vr', 'mr', 'foot']);

/**
 * Desired visibility for the reference object, sourced from the loaded
 * experience (`reference_object_visible`). Because the character loads async
 * (after the project category is known), the loader records the preference here
 * and the component/loader applies it once the object exists. Defaults to shown.
 */
let pendingReferenceVisible = true;

export function shouldShowReferenceObject(category) {
  return REFERENCE_OBJECT_CATEGORIES.has(String(category || '').toLowerCase());
}

/**
 * Set the desired reference-object visibility (e.g. from a loaded experience).
 * Applies immediately if the object already exists; otherwise it's applied when
 * the object next mounts.
 */
export function setReferenceObjectVisiblePreference(visible, scene) {
  pendingReferenceVisible = visible !== false;
  const obj = scene ? getReferenceObject(scene) : null;
  if (obj) {
    obj.visible = pendingReferenceVisible;
    emitReferenceObjectChange(scene);
  }
}

/** Get the live reference object from a scene, if it exists. */
export function getReferenceObject(scene) {
  return scene?.getObjectByName(REFERENCE_OBJECT_NAME) || null;
}

function emitReferenceObjectChange(scene) {
  try {
    const obj = getReferenceObject(scene);
    window.dispatchEvent(
      new CustomEvent(REFERENCE_OBJECT_EVENT, {
        detail: { present: !!obj, visible: obj ? obj.visible !== false : false },
      })
    );
  } catch (_) {
    // no-op: event dispatch is best-effort UI sync
  }
}

/** Toggle (or explicitly set) the reference object's visibility in the scene. */
export function toggleReferenceObjectVisibility(scene, nextVisible) {
  const obj = getReferenceObject(scene);
  if (!obj) return false;
  obj.visible = typeof nextVisible === 'boolean' ? nextVisible : !obj.visible;
  emitReferenceObjectChange(scene);
  return obj.visible;
}

function disposeReferenceObject(scene, root) {
  if (!scene || !root) return;
  scene.remove(root);
  root.traverse?.((obj) => {
    obj.geometry?.dispose?.();
    if (Array.isArray(obj.material)) {
      obj.material.forEach((material) => material?.dispose?.());
    } else {
      obj.material?.dispose?.();
    }
  });
  emitReferenceObjectChange(scene);
}

/** Unlit material so scene lights/HDRI never affect the reference helper. */
function toUnlitMaterial(mat) {
  if (!mat) return mat;
  const basic = new THREE.MeshBasicMaterial({
    color: mat.color?.isColor ? mat.color.clone() : new THREE.Color(0xffffff),
    map: mat.map || null,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
    side: mat.side,
    toneMapped: false,
  });
  mat.dispose?.();
  return basic;
}

/**
 * Load + add the reference character to the scene once. Returns a cleanup fn.
 * Exported so non-React callers (e.g. preview bootstrap) can reuse it too.
 */
export function mountReferenceObject(scene, category) {
  if (!scene) return () => {};

  if (scene.getObjectByName(REFERENCE_OBJECT_NAME)) {
    return () => {
      disposeReferenceObject(scene, scene.getObjectByName(REFERENCE_OBJECT_NAME));
    };
  }

  let cancelled = false;
  let loadedRoot = null;
  const loader = new GLTFLoader();

  const isFoot = category?.toLowerCase() === 'foot';
  const assetUrl = isFoot ? footReferenceAssetUrl : mrDummyCharacterUrl;

  loader.load(
    assetUrl,
    (gltf) => {
      if (cancelled || scene.getObjectByName(REFERENCE_OBJECT_NAME)) {
        disposeReferenceObject(scene, gltf?.scene);
        return;
      }

      const root = gltf.scene || gltf.scenes?.[0];
      if (!root) return;

      let rootToScene;

      if (isFoot) {
        // Create a parent group to hold both foot objects
        const group = new THREE.Group();
        group.name = REFERENCE_OBJECT_NAME;

        // First foot: positioned at x = -5, scale 1.2
        const foot1 = root;
        foot1.position.set(0.08, 0, 0);
        foot1.rotation.set(0, Math.PI, 0);
        foot1.scale.set(0.21, 0.21, 0.21);
        group.add(foot1);

        // Second foot (cloned): positioned at x = +5, scale -1.2
        const foot2 = root.clone();
        foot2.position.set(-0.08, 0, 0);
        foot2.rotation.set(0, 0, Math.PI);
        foot2.scale.set(-0.21, -0.21, -0.21);
        group.add(foot2);

        rootToScene = group;
      } else {
        root.name = REFERENCE_OBJECT_NAME;
        root.position.set(0, 0, 0.2);
        root.rotation.set(0, Math.PI, 0);
        root.scale.set(0.04, 0.04, 0.04);
        rootToScene = root;
      }

      rootToScene.userData.isHelper = true;
      rootToScene.userData.isMrCenterCube = true;
      rootToScene.traverse((obj) => {
        obj.userData = obj.userData || {};
        obj.userData.isHelper = true;
        obj.userData.isMrCenterCube = true;
        obj.raycast = () => {};

        if (!obj.isMesh || !obj.material) return;
        obj.material = Array.isArray(obj.material)
          ? obj.material.map(toUnlitMaterial)
          : toUnlitMaterial(obj.material);
      });

      rootToScene.visible = pendingReferenceVisible;

      loadedRoot = rootToScene;
      scene.add(rootToScene);
      emitReferenceObjectChange(scene);
    },
    undefined,
    (error) => {
      console.warn('[ReferenceObject] Failed to load reference object:', error);
    }
  );

  return () => {
    cancelled = true;
    disposeReferenceObject(
      scene,
      loadedRoot || scene.getObjectByName(REFERENCE_OBJECT_NAME)
    );
  };
}

/**
 * Renders nothing; manages the VR/MR reference character lifecycle in the scene.
 * Usage: <ReferenceObject scene={scene} category={category} />
 */
export default function ReferenceObject({ scene, category }) {
  useEffect(() => {
    if (!scene) return undefined;

    const existing = scene.getObjectByName(REFERENCE_OBJECT_NAME);
    if (!shouldShowReferenceObject(category)) {
      disposeReferenceObject(scene, existing);
      return undefined;
    }

    if (existing) return undefined;

    return mountReferenceObject(scene, category);
  }, [scene, category]);

  return null;
}
